import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaSpinner, FaList } from 'react-icons/fa';
import AuthContext from '../../../context/AuthContext';
import styles from './BulkListingScore.module.css';
import ResultsSection from '../components/ResultsSection';
import ActionPlanSection from '../components/ActionPlanSection';
import ComponentAnalysisSection from '../components/ComponentAnalysisSection';
import QueueNotificationPopup from '../../UI/QueueNotificationPopup';
import SelectProductCatalogueModal from '../../UI/SelectProductCatalogueModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to convert percentage to points (0-100 -> 0-10)
const convertToPoints = (percentage) => {
    if (percentage === null || percentage === undefined) return 0;
    return (percentage / 10).toFixed(1);
};

const BulkListingScore = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    // State management
    const [products, setProducts] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [results, setResults] = useState([]);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [selectedTab, setSelectedTab] = useState('actionPlan');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [showQueuePopup, setShowQueuePopup] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    // Pagination logic
    const totalPages = Math.ceil(products.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = products.slice(startIndex, endIndex);

    // Pagination handlers
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Fetch user's ASINs from database
    const fetchUserAsins = useCallback(async () => {
        if (!authCtx.isLoggedIn) return;

        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/listing_score/user-asins`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.products) {
                setProducts(data.products);
            } else if (data.products === undefined) {
                setError('No products found. Please add some ASINs to your account first.');
            } else {
                setError('Failed to fetch products. Please try again.');
            }
        } catch (err) {
            console.error('Error fetching user ASINs:', err);
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, [authCtx.isLoggedIn, authCtx.token]);

    // Handle bulk analysis - submit and show popup
    const handleBulkAnalyze = useCallback(async () => {
        if (selectedAsins.length === 0) {
            setError('Please select at least one ASIN to analyze.');
            return;
        }

        let response;
        try {
            setSubmitting(true);
            setError('');
            
            response = await fetch(`${API_URL}/listing_score/bulk-analyze`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ asins: selectedAsins, source: 'catalog' })
            });

            // Handle 409 Conflict (duplicate request)
            if (response.status === 409) {
                const errorData = await response.json();
                setError(errorData.message || 'A similar request is already being processed. Please wait a moment and try again.');
                setSubmitting(false);
                return;
            }

            // Handle async task response (202 status) - show popup
            if (response.status === 202) {
                const data = await response.json();
                
                if (data.task_id) {
                    // Update credits in real-time if provided in response
                    if (data.credits && typeof data.credits.remaining === 'number') {
                        authCtx.updateUserCredit(data.credits.remaining);
                    }
                    // Show queue notification popup
                    setShowQueuePopup(true);
                    setSubmitting(false);
                    // Clear selections after successful submission
                    setSelectedAsins([]);
                    return;
                }
            }

            // Handle direct response (if API returns results immediately - unlikely for bulk)
            const data = await response.json();
            
            if (data.success && data.results) {
                // Process results to match expected structure
                const processedResults = data.results.map(result => ({
                    asin: result.asin,
                    analysis: result.analysis?.analysis || result.analysis,
                    product_info: result.analysis?.product_info || result.product_info || {}
                }));
                
                setResults(processedResults);
                setShowResultsModal(true);
                
                // Update user credits if available
                if (data.remaining_credits !== undefined && authCtx.updateUserCredit) {
                    authCtx.updateUserCredit(data.remaining_credits);
                }
            } else {
                setError(data.message || data.error || 'Analysis failed. Please try again.');
            }
        } catch (err) {
            console.error('Error during bulk analysis:', err);
            setError('Network error. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    }, [selectedAsins, authCtx.token, authCtx.updateUserCredit]);

    // Load ASINs on component mount
    useEffect(() => {
        fetchUserAsins();
    }, [fetchUserAsins]);

    useEffect(() => {
        if (location.state?.autoSelectAll && products.length > 0 && !showProductModal) {
            setShowProductModal(true);
            // Auto-select all products
            const allAsins = products.map(p => p.asin).filter(Boolean);
            setSelectedAsins(allAsins);
            setSelectAll(true);
        }
    }, [location.state?.autoSelectAll, products.length, showProductModal]);

    // Note: Auto-analyze removed - users must click Submit button to generate scores

    // Handle ASIN selection
    const handleAsinSelect = useCallback((asin) => {
        if (!asin) return;
        
        setSelectedAsins(prev => {
            // Toggle selection: if already selected, remove it; otherwise add it
            let newSelected;
            if (prev.includes(asin)) {
                newSelected = prev.filter(a => a !== asin);
            } else {
                newSelected = [...prev, asin];
            }
            // Update selectAll state
            if (products.length > 0) {
                setSelectAll(newSelected.length === products.length);
            }
            return newSelected;
        });
    }, [products.length]);

    // Handle select all
    const handleSelectAll = useCallback(() => {
        if (!products || products.length === 0) return;
        
        // If all are selected, deselect all
        if (selectedAsins.length === products.length && products.length > 0) {
            setSelectedAsins([]);
            setSelectAll(false);
        } else {
            // Select all products
            const allAsins = products.map(p => p.asin).filter(Boolean);
            setSelectedAsins(allAsins);
            setSelectAll(true);
        }
    }, [products, selectedAsins.length]);

    // Update selectAll state when selectedAsins changes
    useEffect(() => {
        if (products.length > 0) {
            setSelectAll(selectedAsins.length === products.length);
        }
    }, [selectedAsins.length, products.length]);

    // Modal handlers
    const handleOpenProductModal = () => {
        setShowProductModal(true);
    };

    const handleCloseProductModal = () => {
        setShowProductModal(false);
    };

    const handleModalAnalyze = () => {
        setShowProductModal(false);
        handleBulkAnalyze();
    };

    // Handle viewing individual result
    const handleViewSingle = (asin) => {
        const result = results.find(r => r.asin === asin);
        if (result) {
            setSelectedResult(result);
        }
    };

    // Handle back to results
    const handleBackToResults = () => {
        setSelectedResult(null);
    };

    // Show individual result view
    if (selectedResult) {
        const { asin, analysis } = selectedResult;
        
        return (
            <div className={styles.container}>
                <div className={styles.simpleHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button
                            className={styles.simpleBackButton}
                            onClick={() => setSelectedResult(null)}
                            aria-label="Back to Results"
                        >
                            <FaArrowLeft size={16} />
                            Back
                        </button>
                        <h1 className={styles.simpleTitle}>
                            Listing Score Analyzer
                        </h1>
                    </div>
                </div>

                {analysis && (
                    <div id="listing-score-content">
                        <ResultsSection productData={{ 
                            analysis: analysis, 
                            asin: selectedResult.asin,
                            main_image: selectedResult.product_info?.main_image,
                            b_plus_images: selectedResult.product_info?.b_plus_images || [],
                            title: selectedResult.product_info?.title || '',
                            description: selectedResult.product_info?.description || '',
                            bullet_points: selectedResult.product_info?.bullet_points || [],
                            specifications: selectedResult.product_info?.specifications || [],
                            a_plus_content: selectedResult.product_info?.a_plus_content || false
                        }} />
                        <section className={styles.section2}>
                            <div className={styles.toggleContainer}>
                                <button
                                    className={selectedTab === 'actionPlan' ? `${styles.actionPlanButton} ${styles.activeToggle}` : styles.actionPlanButton}
                                    onClick={() => setSelectedTab('actionPlan')}
                                >
                                    Action Plans
                                </button>
                                <button
                                    className={selectedTab === 'componentAnalysis' ? `${styles.toggleButton} ${styles.activeToggle}` : styles.toggleButton}
                                    onClick={() => setSelectedTab('componentAnalysis')}
                                >
                                    Component Analysis
                                </button>
                            </div>
                            {selectedTab === 'actionPlan' && (
                                <ActionPlanSection 
                                    productData={{ analysis: analysis }} 
                                    showDetailedAnalysis={true}
                                />
                            )}
                            {selectedTab === 'componentAnalysis' && (
                                <ComponentAnalysisSection productData={{ analysis: analysis }} />
                            )}
                        </section>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.bulkListingScore}>
            {/* Simple Header Section */}
            <div className={styles.simpleHeader}>
                <h1 className={styles.simpleTitle}>
                    Bulk Listing Scorer
                </h1>
                <p className={styles.simpleSubtitle}>
                    Select ASINs from the list below, then click Submit to generate comprehensive listing scores, action points, and component analysis
                </p>
            </div>

            {/* Stats Container */}
            <div className={styles.statsContainer}>
                <div className={styles.statCard}>
                    <h3>Total Products</h3>
                    <span>{products.length}</span>
                </div>
                <div className={styles.statCard}>
                    <h3>Selected</h3>
                    <span>{selectedAsins.length}</span>
                </div>
                <div className={styles.statCard}>
                    <h3>Ready to Analyze</h3>
                    <span>{selectedAsins.length > 0 ? 'Yes' : 'No'}</span>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className={styles.errorMessage}>
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading your products...</p>
                </div>
            )}

            {/* Product Selection Section */}
            {!loading && products.length > 0 && (
                <div className={styles.productSelectionSection}>
                    <div className={styles.selectionInfo}>
                        <h3>Select Products to Analyze</h3>
                        <p>Choose products from your catalogue to analyze their listing scores</p>
                        {selectedAsins.length > 0 && (
                            <div className={styles.selectedInfo}>
                                <FaCheck className={styles.checkIcon} />
                                <span>{selectedAsins.length} product{selectedAsins.length !== 1 ? 's' : ''} selected</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleOpenProductModal}
                        className={styles.selectProductsButton}
                    >
                        <FaList />
                        {selectedAsins.length > 0 
                            ? `Update Selection (${selectedAsins.length} selected)`
                            : 'Select from Product Catalogue'
                        }
                    </button>
                    
                    {selectedAsins.length > 0 && (
                        <button 
                            className={styles.analyzeBtn}
                            onClick={handleBulkAnalyze}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <FaSpinner className={styles.spinner} />
                                    Submitting...
                                </>
                            ) : (
                                `Submit (${selectedAsins.length} Product${selectedAsins.length !== 1 ? 's' : ''})`
                            )}
                        </button>
                    )}
                </div>
            )}


            {/* Full Screen Results Page */}
            {showResultsModal && results.length > 0 && (
                <div className={styles.fullScreenResults}>
                    <div className={styles.fullScreenHeader}>
                        <button 
                            className={styles.fullScreenBackButton}
                            onClick={() => setShowResultsModal(false)}
                        >
                            <FaArrowLeft size={16} />
                            Back to Products
                        </button>
                        <h1 className={styles.fullScreenTitle}>
                            Analysis Results
                        </h1>
                    </div>

                    <div className={styles.fullScreenContent}>
                        <div className={styles.resultsGrid}>
                            {results.map((result) => (
                                <div key={result.asin} className={styles.simpleResultCard}>
                                    {/* Card Header */}
                                    <div className={styles.simpleCardHeader}>
                                        <h3 className={styles.simpleAsinTitle}>ASIN: {result.asin}</h3>
                                        {result.analysis?.product_info?.title && (
                                            <p className={styles.simpleProductTitle}>{result.analysis?.product_info?.title}</p>
                                        )}
                                    </div>

                                    {/* Summary */}
                                    <div className={styles.simpleSummary}>
                                        <p>{result.analysis?.overall_analysis || 'No summary available'}</p>
                                    </div>

                                    {/* Overall Score */}
                                    <div className={styles.simpleOverallScore}>
                                        <span className={styles.simpleScoreLabel}>Overall Score</span>
                                        <span className={styles.simpleScoreValue}>
                                            {convertToPoints(result.analysis?.percentage_score || 0)}/10
                                        </span>
                                    </div>

                                    {/* Component Performance Preview */}
                                    <div className={styles.simpleComponentPreview}>
                                        <h4 className={styles.simpleComponentTitle}>Component Performance</h4>
                                        <div className={styles.simpleComponentBars}>
                                            {result.analysis?.component_scores ? (
                                                Object.entries(result.analysis.component_scores).slice(0, 3).map(([component, score]) => (
                                                    <div key={component} className={styles.simpleComponentBar}>
                                                        <span className={styles.simpleComponentName}>
                                                            {component.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                        <span className={styles.simpleComponentScore}>
                                                            {convertToPoints(score.percentage || score.score || 0)}/10
                                                        </span>
                                                    </div>
                                                ))
                                            ) : (
                                                <p>No component scores available</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* View Analysis Button */}
                                    <div className={styles.simpleCardActions}>
                                        <button 
                                            className={styles.simpleViewAnalysisBtn}
                                            onClick={() => handleViewSingle(result.asin)}
                                        >
                                            View Analysis
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Queue Notification Popup */}
            <QueueNotificationPopup
                show={showQueuePopup}
                onClose={() => setShowQueuePopup(false)}
                onResetFiles={() => {
                    setSelectedAsins([]);
                    setShowQueuePopup(false);
                }}
                title="Bulk Listing Score Started"
            />

            {/* Product Selection Modal */}
            <SelectProductCatalogueModal
                show={showProductModal}
                onClose={handleCloseProductModal}
                products={products}
                selectedAsins={selectedAsins}
                onAsinSelect={handleAsinSelect}
                onSelectAll={handleSelectAll}
                onAnalyze={handleModalAnalyze}
                selectAll={selectAll}
            />
        </div>
    );
};

export default BulkListingScore;
